import { type ConversationTranscript, EDITABLE_PHRASE_TYPES, PHRASE_TYPES } from '@/entities/conversations';
import type { EditedInterval } from '@/features/DialogSession';
import type { EditableTranscriptRecord } from './types';

const EDITABLE_TYPES: ReadonlySet<string> = new Set(EDITABLE_PHRASE_TYPES);

const isEditableRecord = (record: ConversationTranscript): record is EditableTranscriptRecord =>
  EDITABLE_TYPES.has(record.type);

/**
 * Лента режима редактирования: каждая правимая запись отдельно, без склейки и без отбора событий,
 * по порядку начала.
 */
export const selectEditableRecords = (transcript: ConversationTranscript[]): EditableTranscriptRecord[] =>
  transcript.filter(isEditableRecord).sort((a, b) => a.startedAt - b.startedAt || a.id - b.id);

/** Реплики расшифровки по id */
export const mapPhrasesById = (records: EditableTranscriptRecord[]): Map<number, ConversationTranscript> =>
  new Map(records.filter((record) => record.type === PHRASE_TYPES.PHRASE).map((record) => [record.id, record]));

/** Число перебиваний, в которых реплика перебитая или перебивающая, — они удаляются вместе с ней */
export const countInterruptionsByPhrase = (records: EditableTranscriptRecord[]): Map<number, number> => {
  const counts = new Map<number, number>();
  for (const record of records) {
    if (record.type !== PHRASE_TYPES.INTERRUPTION) continue;
    // Перебивание, где реплика и перебитая, и перебивающая, удаляется с ней один раз
    const phraseIds = new Set([record.interruptedPhraseId, record.interruptionPhraseId]);
    for (const phraseId of phraseIds) {
      if (phraseId != null) counts.set(phraseId, (counts.get(phraseId) ?? 0) + 1);
    }
  }
  return counts;
};

/**
 * Длительность записи разговора, мс: по плееру, а пока он её не знает — по концу последней записи
 * расшифровки. `playerDurationSec` — длительность из плеера в секундах; не число или ноль — неизвестна.
 */
export const getRecordingDurationMs = (
  records: EditableTranscriptRecord[],
  playerDurationSec: number | undefined,
): number => {
  if (playerDurationSec !== undefined && Number.isFinite(playerDurationSec) && playerDurationSec > 0) {
    return Math.round(playerDurationSec * 1000);
  }
  return records.reduce((max, record) => Math.max(max, record.finishedAt ?? record.startedAt), 0);
};

/**
 * Границы новой записи в промежутке перед записью с индексом `index` (`index` = числу записей —
 * промежуток под последней): конец записи сверху и начало записи снизу, по возрастанию — соседи
 * могут перекрываться. Над первой записью — начало записи разговора, под последней — её конец.
 */
export const getGapInterval = (
  records: EditableTranscriptRecord[],
  index: number,
  recordingDurationMs: number,
): EditedInterval => {
  const above = records[index - 1];
  const below = records[index];
  const from = above ? (above.finishedAt ?? above.startedAt) : 0;
  const to = below ? below.startedAt : recordingDurationMs;
  return { startedAt: Math.min(from, to), finishedAt: Math.max(from, to) };
};

/**
 * Индекс промежутка, в котором открыта форма новой записи: промежуток под записью `afterRecordId`
 * (null — над первой). Запись над промежутком пропала из расшифровки — форма встаёт над первой записью.
 */
export const findGapIndex = (records: EditableTranscriptRecord[], afterRecordId: number | null): number => {
  if (afterRecordId === null) return 0;
  return records.findIndex((record) => record.id === afterRecordId) + 1;
};
